import { Box, Divider, IconButton, Typography, useTheme } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { SalesObjectLevelText } from './SalesObjectLevelText';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExoAlert from '../../../components/exo/ExoAlert';
import { useState } from 'react';
import { API_DELETE, DBRequest, useConfig } from '../../../api/api';
import HttpsIcon from '@mui/icons-material/Https';
import { SalesObjectLevelFiles } from './SalesObjectLevelFiles';
import { ExoMatchingProp } from '../../../components/exo/ExoMatchingProp';
import Feedback from '../../../components/special/Feedback';
import EmailIcon from '@mui/icons-material/Email';
import ExoDialog from '../../../components/exo/ExoDialog';
import { ExoRichTextEditor } from '../../../components/exo/ExoRichTextEditor';
import RenderHTML from '../../../components/preview/RenderHTML';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import MediaPreview from '../../../components/exo/explorer/MediaPreview';
import MarqueeText from '../../../components/special/MarqueeText';
import { RenderEmail } from '../../../components/email-system/ECEmailConversation';

export const SalesObjectLevel = ({
  data,
  onChange,
  onEdit,
  onDelete,
  isEditMode,
  salesObjectId,
  objectProperties,
  onPropsChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const { t } = useTranslation();

  const {
    title,
    description,
    id,
    images,
    documents,
    accessLevel,
    properties,
    sendEmail,
    emailSubject,
    emailContent,
    emailAttachments,
  } = data;

  const [reqDel, setReqDel] = useState(null);
  const [openEmail, setOpenEmail] = useState(false);

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });

  function handleFeedback(text, type) {
    setAlertState({
      alertOpen: true,
      alertText: text,
      alertType: type,
    });
  }

  function handleDelete(element) {
    DBRequest({
      config,
      path: `sales-object-levels/${element.id}`,
      method: API_DELETE,
      onResponse: () => onDelete(element),
    });
  }

  function handleNewSelection(newSelection) {
    onChange({ data: { ...data, properties: newSelection } });
  }

  return (
    <Box
      sx={{
        bgcolor: colors.card,
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        '& .editButton': {
          transition: '0.3s ease',
          bgcolor: colors.success + '30',
          color: colors.success,
          border: `1px solid ${colors.success}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        '& .emailButton': {
          transition: '0.3s ease',
          bgcolor: colors.info + '30',
          color: colors.info,
          border: `1px solid ${colors.info}80`,
          opacity: 1,
          transform: 'translate(-5px)',
          marginLeft: 2,
        },
        ':hover': {
          '& .editButton, & .delButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
      className="px-4 py-2 rounded-lg backdrop-blur-md"
    >
      <Box className="flex w-full justify-between items-start">
        <Typography variant="h4" className="pb-2" fontWeight={600}>
          <span className="monospace">{accessLevel}</span>
          <HttpsIcon className=" ml-1 mr-2" sx={{ fontSize: '16px' }} />
          {title}
          {sendEmail && (
            <IconButton
              onClick={() => setOpenEmail(true)}
              size="small"
              className="emailButton"
            >
              <EmailIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          )}
        </Typography>

        <ExoDialog
          open={openEmail}
          limitWidth
          onClose={() => {
            setOpenEmail(false);
          }}
          className="flex flex-col gap-2"
        >
          <Typography variant="h4">{t('Automatically sent email')}</Typography>
          <RenderEmail
            email={{
              subject: emailSubject,
              htmlBody: emailContent,
              attachments: emailAttachments,
            }}
          />
        </ExoDialog>

        <Box className="flex gap-1">
          <IconButton
            onClick={() => onEdit(data)}
            size="small"
            className="editButton"
          >
            <EditIcon sx={{ fontSize: '15px' }} />
          </IconButton>
          <IconButton
            onClick={() => setReqDel(data)}
            size="small"
            className="delButton"
          >
            <DeleteIcon sx={{ fontSize: '15px' }} />
          </IconButton>
        </Box>
      </Box>

      <Divider />
      <Box className="grid grid-cols-1 md:grid-cols-3 pt-2 gap-2">
        <Box>
          <Typography fontWeight={500}>{t('Properties')}</Typography>
          <ExoMatchingProp
            properties={properties}
            objectProperties={objectProperties}
            noValueEdit
            salesLevelId={id}
            salesObjectId={salesObjectId}
            onFeedback={handleFeedback}
            isEditMode={isEditMode}
            onChange={onPropsChange}
            onSelectionChange={handleNewSelection}
            noGlobalList
            addLabel={t('assign property')}
            allowAdding={false}
          />
        </Box>
        <Box>
          <Typography fontWeight={500}>{t('Info Text')}</Typography>
          <SalesObjectLevelText
            text={description}
            levelId={id}
            onChange={onChange}
            isEditMode={isEditMode}
            onFeedback={handleFeedback}
          />
        </Box>
        <Box>
          <Typography fontWeight={500}>{t('Images')}</Typography>
          <SalesObjectLevelFiles
            files={images}
            levelId={id}
            onChange={onChange}
            fileType="MEDIA"
            arrayKey="images"
            isEditMode={isEditMode}
            onFeedback={handleFeedback}
          />
          <Typography fontWeight={500} className="pt-3">
            {t('Documents')}
          </Typography>
          <SalesObjectLevelFiles
            files={documents}
            levelId={id}
            onChange={onChange}
            fileType="DOCUMENT"
            arrayKey="documents"
            isEditMode={isEditMode}
            onFeedback={handleFeedback}
          />
        </Box>
      </Box>
      <ExoAlert
        onSubmit={() => handleDelete(reqDel)}
        open={Boolean(reqDel)}
        onClose={() => setReqDel(null)}
        alert={{
          submitLabel: t('Delete'),
          message: t('Are you sure you want to permanently delete this entry?'),
        }}
      />
      <Feedback setState={setAlertState} state={alertState} />
    </Box>
  );
};
